import { useQuery, type QueryKey } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { TableV2Footer } from '@/features/shared/components/tableSystem/TableV2Footer';
import {
  resolveTableV2BodyRowsHeight,
  resolveTableV2FillerRowHeight,
} from '@/features/shared/components/tableSystem/TableV2Heights';
import {
  tableV2MinimumVisibleRows,
  tableV2PanelSx,
  tableV2RootSx,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import {
  buildTableV2ResetKey,
  formatTableV2DisplayedRowsLabel,
  resolveTableV2PageSize,
  resolveTableV2PageSizeOptions,
  shouldFillTableV2Rows,
  shouldShowTableV2PaginationControls,
} from '@/features/shared/components/tableSystem/TableV2QueryShellSupport';
import {
  TableV2SkeletonRow,
  type TableV2SkeletonColumnKind,
} from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { s } from '@/theme/tokens';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Text } from '@/ui/primitives';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult } from '@flowqa/shared';

type TableV2DataState = 'empty' | 'error' | 'loading' | 'ready';

const TABLE_V2_EMPTY_TEXT = 'No data';

export type TableV2QueryShellProps<TRow> = {
  active?: boolean;
  ariaLabel: string;
  colgroup: ReactNode;
  emptyText: string;
  entityLabel: string;
  entitySingularLabel?: string;
  fixedFilters?: GridQueryInput['filters'];
  forceLoading?: boolean;
  header: ReactNode;
  initialPageSize?: number;
  minimumVisibleRows?: number;
  pageSizeOptions?: number[];
  queryFn: (input: GridQueryInput) => Promise<QueryResult<TRow>>;
  queryKey: QueryKey;
  requireAuth?: boolean;
  renderRow: (row: TRow, context: { isLastVisibleRow: boolean }) => ReactNode;
  search?: string;
  skeletonColumns: TableV2SkeletonColumnKind[];
  sorts: GridQueryInput['sorts'];
  onQueryMeta?: (meta: { total: number; items: TRow[] } | null) => void;
};

const buildQueryInput = ({
  fixedFilters,
  page,
  pageSize,
  search,
  sorts,
}: {
  fixedFilters?: GridQueryInput['filters'];
  page: number;
  pageSize: number;
  search?: string;
  sorts: GridQueryInput['sorts'];
}): GridQueryInput => ({
  page: page + 1,
  pageSize,
  sorts,
  filters: fixedFilters ?? [],
  search: search?.trim() ? search.trim() : undefined,
});

export const TableV2QueryShell = <TRow extends { id: string }>({
  active = true,
  ariaLabel,
  colgroup,
  emptyText: _emptyText,
  entityLabel,
  entitySingularLabel,
  fixedFilters,
  forceLoading = false,
  header,
  initialPageSize = 10,
  minimumVisibleRows = tableV2MinimumVisibleRows,
  onQueryMeta,
  pageSizeOptions = [10, 25, 50],
  queryFn,
  queryKey,
  requireAuth = true,
  renderRow,
  search,
  skeletonColumns,
  sorts,
}: TableV2QueryShellProps<TRow>): ReactElement => {
  const { session, loading } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => resolveTableV2PageSize(initialPageSize));
  const resolvedPageSizeOptions = useMemo(
    () => resolveTableV2PageSizeOptions(pageSizeOptions),
    [pageSizeOptions],
  );
  const resetKey = useMemo(
    () => buildTableV2ResetKey({ fixedFilters, search, sorts }),
    [fixedFilters, search, sorts],
  );
  const queryInput = useMemo(
    () => buildQueryInput({ fixedFilters, page, pageSize, search, sorts }),
    [fixedFilters, page, pageSize, search, sorts],
  );
  const queryInputKey = useMemo(() => JSON.stringify(queryInput), [queryInput]);
  const resolvedQueryKey = useMemo<QueryKey>(
    () => [
      ...queryKey,
      'v2',
      selectedOrganisationId ?? 'personal',
      selectedProjectId ?? 'all',
      queryInputKey,
    ],
    [queryInputKey, queryKey, selectedOrganisationId, selectedProjectId],
  );
  const rowsQuery = useQuery({
    queryKey: resolvedQueryKey,
    enabled: (requireAuth ? session !== null : true) && active,
    queryFn: async () => await queryFn(queryInput),
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
  });
  const rows = rowsQuery.data?.items ?? [];
  const total = rowsQuery.data?.total ?? 0;
  const displayedFrom = total > 0 ? page * pageSize + 1 : 0;
  const displayedTo = total > 0 ? Math.min(total, (page + 1) * pageSize) : 0;
  const pageCount = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const bodyContentRowCount = rows.length > 0 ? rows.length : 1;
  const columnCount = skeletonColumns.length;
  const showSkeletonRows =
    forceLoading || rowsQuery.isLoading || rowsQuery.isFetching || (requireAuth && loading);
  const dataState: TableV2DataState = rowsQuery.isError
    ? 'error'
    : showSkeletonRows
      ? 'loading'
      : rows.length === 0
        ? 'empty'
        : 'ready';
  const dataError =
    rowsQuery.error instanceof Error ? rowsQuery.error.message : 'Table request failed';
  const shouldFillRows = shouldFillTableV2Rows(total, minimumVisibleRows);
  const fillerRowCount =
    showSkeletonRows || !shouldFillRows ? 0 : Math.max(0, minimumVisibleRows - bodyContentRowCount);
  const bodyHeight = resolveTableV2BodyRowsHeight(minimumVisibleRows);
  const fillerRowHeight = resolveTableV2FillerRowHeight(fillerRowCount);
  const displayedRowsLabel = formatTableV2DisplayedRowsLabel({
    displayedFrom,
    displayedTo,
    entityLabel,
    entitySingularLabel,
    pageSize,
    total,
  });

  useEffect(() => {
    if (!active) return;
    setPage(0);
  }, [active, resetKey]);

  useEffect(() => {
    if (!active) return;
    onQueryMeta?.(
      rowsQuery.data ? { total: rowsQuery.data.total, items: rowsQuery.data.items } : null,
    );
  }, [active, onQueryMeta, rowsQuery.data]);

  const handlePageChange = (nextPage: number): void => {
    if (nextPage < 0 || nextPage >= pageCount || nextPage === page) return;
    setPage(nextPage);
  };

  return (
    <Box sx={tableV2PanelSx}>
      <Box
        data-testid="table-v2-ready"
        data-state={dataState}
        data-error={dataState === 'error' ? dataError : ''}
        sx={{ display: 'none' }}
      />
      <Table aria-label={ariaLabel} data-testid="table-v2-root" sx={tableV2RootSx}>
        {colgroup}
        <TableHead>{header}</TableHead>
        <TableBody>
          {showSkeletonRows ? (
            Array.from({ length: pageSize }, (_item, index) => (
              <TableV2SkeletonRow
                key={`${ariaLabel}-skeleton-${String(index)}`}
                columns={skeletonColumns}
                isLastVisibleRow={index === pageSize - 1}
                rowIndex={index}
              />
            ))
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columnCount}
                sx={{
                  borderBottom: '0 !important',
                  height: bodyHeight,
                  p: s('none'),
                }}
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    height: '100%',
                    justifyContent: 'center',
                  }}
                >
                  <Text role="body" color="textSecondary">
                    {TABLE_V2_EMPTY_TEXT}
                  </Text>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            rows.map(
              (row, index): ReactNode =>
                renderRow(row, {
                  isLastVisibleRow: fillerRowCount === 0 && index === rows.length - 1,
                }),
            )
          )}
          {Array.from({ length: fillerRowCount }, (_item, index) => {
            const isLastFillerRow = index === fillerRowCount - 1;
            return (
              <TableRow key={`${ariaLabel}-filler-${String(index)}`}>
                <TableCell
                  colSpan={columnCount}
                  sx={{
                    borderBottom: isLastFillerRow
                      ? '0 !important'
                      : (theme) => `1px solid ${theme.app.colors.border}`,
                    fontSize: 0,
                    height: fillerRowHeight,
                    lineHeight: 0,
                    p: s('none'),
                  }}
                />
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TableV2Footer
        label={displayedRowsLabel}
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        pageSizeOptions={resolvedPageSizeOptions}
        showControls={shouldShowTableV2PaginationControls(total)}
        onPageChange={handlePageChange}
        onPageSizeChange={(nextPageSize) => {
          setPage(0);
          setPageSize(resolveTableV2PageSize(nextPageSize));
        }}
      />
    </Box>
  );
};
